/**
 * Touch-optimized Condition Selector
 *
 * Large touch targets (min 48x48px) for mobile devices.
 * Supports both tap and swipe gestures.
 */

import { Box, Typography } from '@mui/material';
import { alpha, useTheme, type Theme } from '@mui/material/styles';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningIcon from '@mui/icons-material/Warning';
import CancelIcon from '@mui/icons-material/Cancel';
import { useTranslation } from 'react-i18next';
import type { InspectionItemCondition } from '@/types';

interface TouchConditionSelectorProps {
  value: InspectionItemCondition | null;
  onChange: (condition: InspectionItemCondition) => void;
  disabled?: boolean;
  size?: 'medium' | 'large';
  showLabels?: boolean;
  layout?: 'horizontal' | 'grid';
}

interface ConditionConfigEntry {
  color: string;
  bgColor: string;
  labelKey: string;
  shortLabel: string;
  icon: typeof CheckCircleIcon;
}

// Theme-independent metadata for each condition. Colors are added per-theme via
// getConditionConfig() so they follow the brand palette and adapt to light/dark.
const conditionMeta: Record<
  InspectionItemCondition,
  { labelKey: string; shortLabel: string; icon: typeof CheckCircleIcon }
> = {
  good: { labelKey: 'condition.good', shortLabel: 'OK', icon: CheckCircleIcon },
  fair: { labelKey: 'condition.fair', shortLabel: 'KT', icon: WarningIcon },
  poor: { labelKey: 'condition.poor', shortLabel: 'HK', icon: WarningIcon },
  critical: { labelKey: 'condition.critical', shortLabel: 'KR', icon: CancelIcon },
};

// Brand semantic colours; bgColor is an alpha tint of the foreground so it
// adapts to both light and dark backgrounds (a fixed light tint would read
// wrong on dark surfaces).
const getConditionConfig = (
  theme: Theme
): Record<InspectionItemCondition, ConditionConfigEntry> => {
  const colors: Record<InspectionItemCondition, string> = {
    good: theme.palette.success.main, // success
    fair: theme.palette.warning.main, // warning
    poor: theme.palette.warning.dark, // warning.dark
    critical: theme.palette.error.main, // error
  };
  return {
    good: { ...conditionMeta.good, color: colors.good, bgColor: alpha(colors.good, 0.15) },
    fair: { ...conditionMeta.fair, color: colors.fair, bgColor: alpha(colors.fair, 0.15) },
    poor: { ...conditionMeta.poor, color: colors.poor, bgColor: alpha(colors.poor, 0.15) },
    critical: {
      ...conditionMeta.critical,
      color: colors.critical,
      bgColor: alpha(colors.critical, 0.15),
    },
  };
};

// Backwards-compatible export (metadata only; colors come from getConditionConfig).
const conditionConfig = conditionMeta;

const sizeConfig = {
  medium: {
    minSize: 48,
    iconSize: 28,
    padding: 1.5,
  },
  large: {
    minSize: 64,
    iconSize: 36,
    padding: 2,
  },
};

export function TouchConditionSelector({
  value,
  onChange,
  disabled = false,
  size = 'large',
  showLabels = true,
  layout = 'horizontal',
}: TouchConditionSelectorProps) {
  const { t } = useTranslation('inspection');
  const theme = useTheme();
  const conditionConfig = getConditionConfig(theme);
  const conditions: InspectionItemCondition[] = ['good', 'fair', 'poor', 'critical'];
  const { minSize, iconSize, padding } = sizeConfig[size];

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: layout === 'grid' ? 'row' : 'row',
        flexWrap: layout === 'grid' ? 'wrap' : 'nowrap',
        gap: 1.5,
        justifyContent: layout === 'grid' ? 'space-between' : 'flex-start',
      }}
    >
      {conditions.map((condition) => {
        const config = conditionConfig[condition];
        const Icon = config.icon;
        const isSelected = value === condition;

        return (
          <Box
            key={condition}
            onClick={() => !disabled && onChange(condition)}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 0.5,
              p: padding,
              minWidth: minSize,
              minHeight: minSize,
              borderRadius: 2,
              border: 2,
              borderColor: isSelected ? config.color : 'divider',
              backgroundColor: isSelected ? config.bgColor : 'background.paper',
              cursor: disabled ? 'not-allowed' : 'pointer',
              opacity: disabled ? 0.5 : 1,
              transition: 'all 0.15s ease-out',
              WebkitTapHighlightColor: 'transparent',
              touchAction: 'manipulation',
              userSelect: 'none',
              flex: layout === 'grid' ? '1 1 calc(50% - 6px)' : '0 0 auto',
              boxShadow: isSelected ? `0 0 0 3px ${alpha(config.color, 0.125)}` : 'none',
              '&:active': !disabled
                ? {
                    transform: 'scale(0.95)',
                    backgroundColor: config.bgColor,
                  }
                : undefined,
            }}
          >
            <Icon
              sx={{
                fontSize: iconSize,
                color: isSelected ? config.color : 'text.disabled',
                transition: 'color 0.15s',
              }}
            />
            {showLabels && (
              <Typography
                variant="caption"
                sx={{
                  fontSize: size === 'large' ? '0.75rem' : '0.65rem',
                  fontWeight: isSelected ? 600 : 400,
                  color: isSelected ? config.color : 'text.secondary',
                  textAlign: 'center',
                  lineHeight: 1.2,
                }}
              >
                {t(config.labelKey)}
              </Typography>
            )}
          </Box>
        );
      })}
    </Box>
  );
}

/**
 * Compact condition badge for display
 */
interface ConditionBadgeProps {
  condition: InspectionItemCondition | null;
  size?: 'small' | 'medium';
}

export function ConditionBadge({ condition, size = 'medium' }: ConditionBadgeProps) {
  const { t } = useTranslation('inspection');
  const theme = useTheme();

  if (!condition) {
    return (
      <Box
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          px: 1,
          py: 0.5,
          borderRadius: 1,
          backgroundColor: 'action.hover',
        }}
      >
        <Typography
          variant="caption"
          sx={{
            fontSize: size === 'small' ? '0.65rem' : '0.75rem',
            color: 'text.disabled',
          }}
        >
          {t('condition.notInspected')}
        </Typography>
      </Box>
    );
  }

  const config = getConditionConfig(theme)[condition];
  const Icon = config.icon;
  const iconSize = size === 'small' ? 14 : 18;

  return (
    <Box
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        px: 1,
        py: 0.5,
        borderRadius: 1,
        backgroundColor: config.bgColor,
      }}
    >
      <Icon sx={{ fontSize: iconSize, color: config.color }} />
      <Typography
        variant="caption"
        sx={{
          fontSize: size === 'small' ? '0.65rem' : '0.75rem',
          fontWeight: 600,
          color: config.color,
        }}
      >
        {t(config.labelKey)}
      </Typography>
    </Box>
  );
}

export { conditionConfig, getConditionConfig };
